import type { ModelName, SupportedSession } from "../../types";
import type { Session } from "@tellescope/sdk";
import { useTellescopeSession } from "../../context";
import type { AccessAction, AccessType } from "@tellescope/types-models";

export type AccessOperation = keyof Session["userInfo"]["access"][ModelName];

/**
 * Type guard to check if a session is a user Session (has `userInfo.access`).
 * User sessions have `access` on `userInfo`, enduser sessions do not.
 */
const isUserSession = (session: SupportedSession): session is Session => "access" in session.userInfo;

/** @deprecated Use `UsePermissionQueryArgs` instead. */
export interface UsePermissionArgs<Model extends ModelName> {
  model: Model;
  action: AccessAction;
  session?: SupportedSession;
  validPermissions?: AccessType[];
}

/**
 * @deprecated Use `usePermissionQuery` instead. It supports multiple actions,
 * per-record evaluation via a model→evaluator registry, optional internal
 * record fetching via `recordId`, and fetching related endusers as needed.
 */
export const usePermission = <Model extends ModelName>({
  model,
  action,
  session: providedSession,
  validPermissions,
}: UsePermissionArgs<Model>) => {
  const session = useTellescopeSession(providedSession);

  if (!session || !isUserSession(session)) return { access: false, type: null };

  const access = session.userInfo.access[model]?.[action];
  if (access === undefined) return { access: false, type: null };

  if (validPermissions?.length) {
    return { access: validPermissions.includes(access), type: access };
  }

  return { access: !!access, type: access };
};
